import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconGasStation,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import GeoSelectField from '../shared/GeoSelectField';
import { geoCascade } from '../shared/bdGeo';
import FuelCardPreviewFrame from './FuelCardPreviewFrame';
import FuelCardFormInfoBox from './FuelCardFormInfoBox';
import {
    FUEL_TYPE_VALUES,
    INITIAL_FUEL_CARD_FORM,
    displayField,
    getFuelCardFieldSuggestions,
    getFuelCardFormSnapshot,
    getEmptyFuelCardFields,
    getMobileValidationError,
    isFuelCardFormComplete,
    normalizeBdMobile,
    normalizeDigits,
    saveFuelCardFieldHistory,
    saveFuelCardFormSnapshot,
} from './fuelCardFormEngine';
import { downloadFuelCardPdf, printFuelCardForm } from './generateFuelCardPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-3.5 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {children}
        </div>
    );
}

function SuggestInput({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    suggestions = [],
    inputMode,
    type = 'text',
    maxLength,
    autoComplete,
    listId,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                list={listId}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass}`}
            />
            {listId && suggestions.length > 0 ? (
                <datalist id={listId}>
                    {suggestions.map((item) => (
                        <option key={item} value={item} />
                    ))}
                </datalist>
            ) : null}
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function FuelCardFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_FUEL_CARD_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [mobileError, setMobileError] = useState('');
    const [mobileTouched, setMobileTouched] = useState(false);
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getFuelCardFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.applicantName) || displayField(next.fatherOrProprietor)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveFuelCardFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            if (!displayField(value)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const handleDigitsChange = (key, raw, maxLength) => {
        update(key, normalizeDigits(raw, maxLength));
    };

    const handleMobileChange = (raw) => {
        const digits = normalizeBdMobile(raw);
        update('mobile', digits);
        if (mobileTouched) {
            setMobileError(getMobileValidationError(digits, t));
        }
    };

    const validateMobile = () => {
        setMobileTouched(true);
        if (!form.mobile) {
            setMobileError('');
            return true;
        }
        const err = getMobileValidationError(form.mobile, t);
        setMobileError(err);
        return !err;
    };

    const mobileInputError = fieldErrors.mobile || (mobileTouched ? mobileError : '');

    /**
     * Remember what was typed, then export — whatever state the form is in.
     *
     * Missing fields deliberately do NOT block the download: a common use of
     * this tool is printing the form blank, or half-filled, to complete the rest
     * by hand at the office. Downloading one only asks for a confirmation that
     * names what is still empty, and printing does not even ask.
     */
    const prepareForExport = () => {
        setFieldErrors({});
        saveFuelCardFieldHistory('fatherOrProprietor', form.fatherOrProprietor);
        saveFuelCardFieldHistory('nidOrLicense', form.nidOrLicense);
        saveFuelCardFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyFuelCardFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('fuelCardForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/fuel-card-form', { category: 'general' });
            const base = form.applicantName.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadFuelCardPdf(previewRef.current, `fuel-card-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('fuelCardForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('fuelCardForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = () => {
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        recordToolUsage('general/fuel-card-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('fuelCardForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printFuelCardForm(previewRef.current, t('fuelCardForm.errors.popupBlocked'));
            notifications.show({
                title: t('fuelCardForm.printTitle'),
                message: t('fuelCardForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('fuelCardForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_FUEL_CARD_FORM);
        setMobileError('');
        setMobileTouched(false);
        setFieldErrors({});
        notifications.show({
            title: t('fuelCardForm.clearedTitle'),
            message: t('fuelCardForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getFuelCardFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('fuelCardForm.noSnapshotTitle'),
                message: t('fuelCardForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        setMobileError('');
        setMobileTouched(false);
        setFieldErrors({});
        notifications.show({
            title: t('fuelCardForm.restoredTitle'),
            message: t('fuelCardForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => key !== 'fuelType' && displayField(value)),
        [form],
    );

    const isComplete = useMemo(() => isFuelCardFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('fuelCardForm.statusReady'),
        success: t('fuelCardForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`fuelCardForm.howTo.step${i}`)),
        [t],
    );

    // District first, then its upazilas (see shared/bdGeo).
    const geo = geoCascade(form, update, { district: 'district', upazila: 'thanaUpazila' });

    const fieldError = (key) => {
        const err = fieldErrors[key];
        if (!err) return undefined;
        if (key === 'mobile') return err;
        return ' ';
    };

    const suggestionsFor = (key) => getFuelCardFieldSuggestions(key);

    return (
        <>
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/fuel-card-form"
            subtitleKey="fuelCardForm.subtitle"
            // 30/70: the letter is what the applicant is checking as they
            // type, and an A4 page needs the room to stay legible, so the
            // fields take the narrow column and the preview becomes the
            // sticky rail beside them. The 9px is half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('fuelCardForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('fuelCardForm.clearAll')}
            primaryAction={{
                label: t('fuelCardForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            // Print and Restore ride in the sticky bar with Download and Clear,
            // so every action on the form is in one place at the foot of the
            // screen rather than parked beside a column you have scrolled past.
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('fuelCardForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('fuelCardForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection number={t('fuelCardForm.sections.personal.number')} title={t('fuelCardForm.sections.personal.title')}>
                        <div className="space-y-3">
                            <div>
                                <FormLabel htmlFor="applicantName">{t('fuelCardForm.fields.applicantName')}</FormLabel>
                                <SuggestInput
                                    id="applicantName"
                                    name="applicantName"
                                    value={form.applicantName}
                                    onChange={(v) => update('applicantName', v)}
                                    placeholder={t('fuelCardForm.placeholders.applicantName')}
                                    error={fieldError('applicantName')}
                                    suggestions={suggestionsFor('applicantName')}
                                    listId="fuel-card-applicantName"
                                    autoComplete="name"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="fatherOrProprietor">{t('fuelCardForm.fields.fatherOrProprietor')}</FormLabel>
                                    <SuggestInput
                                        id="fatherOrProprietor"
                                        name="fatherOrProprietor"
                                        value={form.fatherOrProprietor}
                                        onChange={(v) => update('fatherOrProprietor', v)}
                                        placeholder={t('fuelCardForm.placeholders.fatherOrProprietor')}
                                        error={fieldError('fatherOrProprietor')}
                                        suggestions={suggestionsFor('fatherOrProprietor')}
                                        listId="fuel-card-fatherOrProprietor"
                                        autoComplete="father-name"
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="nidOrLicense">{t('fuelCardForm.fields.nidOrLicense')}</FormLabel>
                                    <SuggestInput
                                        id="nidOrLicense"
                                        name="nidOrLicense"
                                        value={form.nidOrLicense}
                                        onChange={(v) => handleDigitsChange('nidOrLicense', v, 20)}
                                        placeholder={t('fuelCardForm.placeholders.nidOrLicense')}
                                        error={fieldError('nidOrLicense')}
                                        suggestions={suggestionsFor('nidOrLicense')}
                                        listId="fuel-card-nidOrLicense"
                                        inputMode="numeric"
                                        maxLength={20}
                                        autoComplete="national-id"
                                    />
                                </div>
                            </div>

                            <div>
                                <FormLabel htmlFor="mobile">{t('fuelCardForm.fields.mobile')}</FormLabel>
                                <SuggestInput
                                    id="mobile"
                                    name="mobile"
                                    value={form.mobile}
                                    onChange={handleMobileChange}
                                    onBlur={validateMobile}
                                    placeholder={t('fuelCardForm.placeholders.mobile')}
                                    error={mobileInputError}
                                    inputMode="tel"
                                    type="tel"
                                    maxLength={11}
                                    autoComplete="tel"
                                />
                            </div>
                        </div>
                    </FormSection>

                    <FormSection number={t('fuelCardForm.sections.address.number')} title={t('fuelCardForm.sections.address.title')}>
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="villageRoad">{t('fuelCardForm.fields.villageRoad')}</FormLabel>
                                    <SuggestInput
                                        id="villageRoad"
                                        name="villageRoad"
                                        value={form.villageRoad}
                                        onChange={(v) => update('villageRoad', v)}
                                        placeholder={t('fuelCardForm.placeholders.villageRoad')}
                                        error={fieldError('villageRoad')}
                                        suggestions={suggestionsFor('villageRoad')}
                                        listId="fuel-card-villageRoad"
                                        autoComplete="street-address"
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="union">{t('fuelCardForm.fields.union')}</FormLabel>
                                    <SuggestInput
                                        id="union"
                                        name="union"
                                        value={form.union}
                                        onChange={(v) => update('union', v)}
                                        placeholder={t('fuelCardForm.placeholders.union')}
                                        error={fieldError('union')}
                                        suggestions={suggestionsFor('union')}
                                        listId="fuel-card-union"
                                        autoComplete="organization"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="postOffice">{t('fuelCardForm.fields.postOffice')}</FormLabel>
                                    <SuggestInput
                                        id="postOffice"
                                        name="postOffice"
                                        value={form.postOffice}
                                        onChange={(v) => update('postOffice', v)}
                                        placeholder={t('fuelCardForm.placeholders.postOffice')}
                                        error={fieldError('postOffice')}
                                        suggestions={suggestionsFor('postOffice')}
                                        listId="fuel-card-postOffice"
                                        autoComplete="address-line2"
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="district">{t('fuelCardForm.fields.district')}</FormLabel>
                                    <GeoSelectField
                                        {...geo.district}
                                        selectOnly
                                        placeholder={t('fuelCardForm.placeholders.district')}
                                        error={fieldError('district')}
                                        inputClassName={`${inputBaseClass} ${fieldError('district') ? inputErrClass : inputOkClass}`}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="thanaUpazila">{t('fuelCardForm.fields.thanaUpazila')}</FormLabel>
                                    <GeoSelectField
                                        {...geo.upazila}
                                        placeholder={t('fuelCardForm.placeholders.thanaUpazila')}
                                        error={fieldError('thanaUpazila')}
                                        inputClassName={`${inputBaseClass} ${fieldError('thanaUpazila') ? inputErrClass : inputOkClass}`}
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="pumpName">{t('fuelCardForm.fields.pumpName')}</FormLabel>
                                    <SuggestInput
                                        id="pumpName"
                                        name="pumpName"
                                        value={form.pumpName}
                                        onChange={(v) => update('pumpName', v)}
                                        placeholder={t('fuelCardForm.placeholders.pumpName')}
                                        error={fieldError('pumpName')}
                                        suggestions={suggestionsFor('pumpName')}
                                        listId="fuel-card-pumpName"
                                    />
                                </div>
                            </div>
                        </div>
                    </FormSection>

                    <FormSection number={t('fuelCardForm.sections.usage.number')} title={t('fuelCardForm.sections.usage.title')}>
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="machineName">{t('fuelCardForm.fields.machineName')}</FormLabel>
                                    <SuggestInput
                                        id="machineName"
                                        name="machineName"
                                        value={form.machineName}
                                        onChange={(v) => update('machineName', v)}
                                        placeholder={t('fuelCardForm.placeholders.machineName')}
                                        error={fieldError('machineName')}
                                        suggestions={suggestionsFor('machineName')}
                                        listId="fuel-card-machineName"
                                    />
                                </div>
                                <div>
                                    <FormLabel>{t('fuelCardForm.fields.fuelType')}</FormLabel>
                                    <Select
                                        value={form.fuelType}
                                        onValueChange={(v) => update('fuelType', v)}
                                    >
                                        <SelectTrigger className={`h-[42px] rounded-xl ${fieldErrors.fuelType ? 'border-red-500' : ''}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {FUEL_TYPE_VALUES.map((value) => (
                                                <SelectItem key={value} value={value}>
                                                    {t(`fuelCardForm.fuelTypes.${value}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                    <FormLabel htmlFor="fuelLiters">{t('fuelCardForm.fields.fuelLiters')}</FormLabel>
                                    <SuggestInput
                                        id="fuelLiters"
                                        name="fuelLiters"
                                        value={form.fuelLiters}
                                        onChange={(v) => handleDigitsChange('fuelLiters', v, 5)}
                                        placeholder={t('fuelCardForm.placeholders.fuelLiters')}
                                        error={fieldError('fuelLiters')}
                                        inputMode="numeric"
                                        maxLength={5}
                                    />
                                </div>
                                <div>
                                    <FormLabel htmlFor="purpose">{t('fuelCardForm.fields.purpose')}</FormLabel>
                                    <SuggestInput
                                        id="purpose"
                                        name="purpose"
                                        value={form.purpose}
                                        onChange={(v) => update('purpose', v)}
                                        placeholder={t('fuelCardForm.placeholders.purpose')}
                                        error={fieldError('purpose')}
                                        suggestions={suggestionsFor('purpose')}
                                        listId="fuel-card-purpose"
                                    />
                                </div>
                            </div>
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                // A flex column, not a plain block: the preview panel stretches
                // itself, and inside a block wrapper that height would resolve
                // against nothing.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('fuelCardForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('fuelCardForm.previewEmpty')}
                        idleHint={t('fuelCardForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a card taller than the screen
                            cannot stay pinned, so the letter scrolls inside it. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconGasStation size={14} stroke={1.8} />
                                {t('fuelCardForm.livePreview')}
                            </div>
                            <FuelCardPreviewFrame form={form} previewRef={previewRef} zoom={pageZoom.zoom} />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<FuelCardFormInfoBox />}
        />
        </>
    );
}
